/**
 * PharmacyOpsTab.tsx — the ADMIN "Pharmacy" section: the one place the team goes
 * when the external pharmacy has a problem (G4, 2026-09-13).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribed-medication truth.
 *
 * WHY A SECTION, AND WHY IT DID NOT EXIST BEFORE. Every pharmacy-facing control
 * this console had was per-ORDER and lived inside the pipeline deal drawer — you
 * could only reach it by already knowing which patient the pharmacy was ringing
 * about. The recall is not per-order: it is one sweep across everything still
 * sitting in Pharmacy Express's queue, and there was nowhere in the information
 * architecture it could honestly go. Payments is money and this moves none;
 * Pipeline is one deal at a time; the Audit log is a read. So the estate grew
 * the surface it was missing: an admin who has the pharmacy on the phone now has
 * a rail item called Pharmacy, and the answer is inside it.
 *
 * GLOBAL, NOT CLINIC-SCOPED — and that is a truth claim, not a convenience.
 * The recall route takes no clinic and sweeps the whole tenant, so filing it
 * under `?clinic=weight-loss` would imply a narrowing the server does not do.
 * It sits beside Notify requests in AdminPage's GLOBAL_TABS for exactly that
 * reason.
 *
 * R19/money: nothing on this section renders a patient, a medication, a strength
 * or a penny figure. The only server read behind it returns a count.
 */
import { Link } from 'react-router-dom';
import { SectionTitle } from '../staff/ui';
import { Card, Help } from '../ui/kit';
import { CardHeader } from '../ui/sections';
import { RecallUnvouchedAction } from './RecallUnvouchedAction';

export function PharmacyOpsTab() {
  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      <div>
        <SectionTitle>Pharmacy</SectionTitle>
        <Help style={{ marginBottom: 0 }}>
          Operational controls for orders that have gone out to Pharmacy Express. Nothing on this screen emails the
          pharmacy or the patient.
        </Help>
      </div>

      <Card>
        <CardHeader
          title="Orders the pharmacy cannot dispense"
          subtitle="Pull orders with no prescriber-chosen strength back to the clinician lane"
        />
        <RecallUnvouchedAction />
      </Card>

      <Card>
        <CardHeader
          title="The pharmacy cannot sign in"
          subtitle="Their sign-in link works once and expires"
        />
        {/* NOT A SECOND COPY OF THE CONTROL — a signpost to the one that exists.
            A fresh sign-in link is minted PER ORDER (the server refuses one for
            an order it has never sent to the pharmacy), so the control belongs
            on the deal and duplicating it here would mean inventing an order
            picker whose only job is to reach a button that is already reachable. */}
        <Help style={{ marginBottom: 0 }}>
          A fresh sign-in link is issued against a specific order. Open the patient&rsquo;s deal in the{' '}
          <Link to="/admin?tab=pipeline">Pipeline</Link> and use &ldquo;Get a pharmacy sign-in link&rdquo; in the journey
          panel. Do not open the link yourself — the first browser to open it uses it up, and the pharmacy is then
          locked out.
        </Help>
      </Card>
    </div>
  );
}
